import * as Sentry from '@sentry/react';
import { getAPIErrorCode, useCreateIdeaMutation, useGetIdeaQuery, useSearchIdeasQuery } from '@vizit/api';
import type { IdeaStatus, ImageView } from '@vizit/api';
import { FEATURE_FLAGS, SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import { useFeatureFlag } from '@vizit/feature-flags';
import * as React from 'react';
import { isHeroScoredForSpark, isSparkNotReadyError } from '../../utils';
import {
  API_IDEA_STATUS,
  API_IMAGE_TYPE,
  IdeaStatus_,
  POLLING_INTERVAL_MS,
  POLLING_TIMEOUT_MS,
  SparkState,
  canProcessSearchResults,
  findLatestCompletedIdea,
  findLatestInProgressIdea,
  shouldAutoGenerate,
  useSparksTabState,
} from './asset-details-sparks-tab-panel.state';

const SPARK_SENTRY_TAGS = {
  feature: SENTRY_FEATURE_TAGS.Asset,
  stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Asset].assetSparksGeneration,
} as const;

type SentryLevel = 'info' | 'warning' | 'error';

const captureSparkException = (error: Error | unknown, level: SentryLevel, extra: Record<string, unknown>): void => {
  Sentry.captureException(error, { level, tags: SPARK_SENTRY_TAGS, extra });
};

const addSparkBreadcrumb = (message: string, data: Record<string, unknown>): void => {
  Sentry.addBreadcrumb({ message, level: 'info', data });
};

interface UseSparkDataProps {
  workspaceId: string;
  assetId: string;
  currentTab: string;
  imageData: ImageView | null;
  isImageLoading: boolean;
  /** The image's score under its collection's custom pair, which counts as a hero's score */
  customPairScore?: number | null;
}

interface UseSparkDataReturn {
  sparkState: SparkState;
  sparkContent: string | null;
  generateIdea: () => Promise<void>;
  shouldRunGeneration: boolean;
}

export function useSparkData(props: UseSparkDataProps): UseSparkDataReturn {
  const { workspaceId, assetId, currentTab, imageData, isImageLoading, customPairScore = null } = props;

  const isHeroSparkIdeasEnabled = useFeatureFlag(FEATURE_FLAGS.HERO_SPARK_IDEAS, false);

  const createIdeaMutation = useCreateIdeaMutation();
  const {
    state,
    sparkState,
    reset,
    setUnavailableHero,
    setUnavailableNoScore,
    setUnavailableNotReady,
    startLoading,
    startPolling,
    complete,
    setError,
    timeout,
  } = useSparksTabState({ assetId });

  const sparkContent = state.content;
  const currentIdeaId = state.ideaId;
  const isGeneratingNew = createIdeaMutation.isPending;
  const isPolling = state.status === IdeaStatus_.Polling;

  // Effect 1: Reset state when asset changes
  React.useEffect(() => {
    if (assetId && state.assetId !== assetId) {
      reset(assetId);
    }
  }, [assetId, state.assetId, reset]);

  // Effect 2: Handle image eligibility (hero flag/score, omit_score, idle->loading)
  React.useEffect(() => {
    if (!imageData || isImageLoading) return;
    // A score can land after the hero was parked, such as its collection's pair score from the header's poll.
    if (
      state.status === IdeaStatus_.UnavailableNoScore &&
      imageData.image_type === API_IMAGE_TYPE.Hero &&
      !imageData.omit_score &&
      isHeroSparkIdeasEnabled &&
      isHeroScoredForSpark(imageData, customPairScore)
    ) {
      startLoading();
      return;
    }
    if (state.status !== IdeaStatus_.Idle && state.status !== IdeaStatus_.Loading) return;

    if (imageData.image_type === API_IMAGE_TYPE.Hero) {
      if (!isHeroSparkIdeasEnabled) {
        setUnavailableHero();
        return;
      }
      if (!isHeroScoredForSpark(imageData, customPairScore)) {
        // Tab is hidden in the UI for unscored hero, but the hook still runs.
        // Park the state machine so deep-links can't fire backend calls.
        setUnavailableNoScore();
        return;
      }
    }

    if (imageData.omit_score) {
      setUnavailableNoScore();
      return;
    }

    if (state.status === IdeaStatus_.Idle) {
      startLoading();
    }
  }, [
    imageData,
    isImageLoading,
    state.status,
    isHeroSparkIdeasEnabled,
    customPairScore,
    setUnavailableHero,
    setUnavailableNoScore,
    startLoading,
  ]);

  const shouldRunSpark = React.useMemo(() => {
    if (!workspaceId || !assetId || !imageData) return false;
    if (imageData.omit_score) return false;
    if (imageData.image_type === API_IMAGE_TYPE.Carousel) return true;
    if (imageData.image_type === API_IMAGE_TYPE.Hero) {
      return isHeroSparkIdeasEnabled && isHeroScoredForSpark(imageData, customPairScore);
    }
    return false;
  }, [workspaceId, assetId, imageData, isHeroSparkIdeasEnabled, customPairScore]);

  const searchParams = React.useMemo(
    () => ({
      workspace_id: workspaceId || '',
      image_id: assetId,
      limit: 10,
      offset: 0,
      sort_by: 'created_at' as const,
      sort_direction: 'desc' as const,
    }),
    [workspaceId, assetId],
  );

  const {
    data: searchIdeasData,
    isLoading: isSearchIdeasLoading,
    error: searchIdeasError,
  } = useSearchIdeasQuery(searchParams, {
    enabled: shouldRunSpark,
    refetchOnWindowFocus: true,
  });

  // Effect 3: Force re-evaluation when tab becomes visible and we're stuck in loading
  React.useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && state.status === IdeaStatus_.Loading && searchIdeasData && !isSearchIdeasLoading) {
        const ideas = searchIdeasData.data;
        if (!ideas?.length) return;

        const latestCompleted = findLatestCompletedIdea(ideas);
        if (latestCompleted?.output) {
          complete(latestCompleted.output);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [state.status, searchIdeasData, isSearchIdeasLoading, complete]);

  const getIdeaParams = React.useMemo(
    () => ({
      workspace_id: workspaceId || '',
      image_id: assetId,
      idea_id: currentIdeaId || '',
    }),
    [workspaceId, assetId, currentIdeaId],
  );

  const { data: getIdeaData, error: getIdeaError } = useGetIdeaQuery(getIdeaParams, {
    enabled: isPolling && !!currentIdeaId && !!workspaceId && !!assetId,
    refetchIntervalInBackground: true,
    refetchInterval: (query) => {
      const ideaStatus: IdeaStatus | undefined = query.state.data?.status;
      if (ideaStatus === API_IDEA_STATUS.Requested || ideaStatus === API_IDEA_STATUS.Processing) {
        if (state.pollingStartedAt && Date.now() - state.pollingStartedAt > POLLING_TIMEOUT_MS) {
          captureSparkException(new Error('Spark idea polling timeout'), 'warning', {
            asset_id: assetId,
            idea_id: currentIdeaId,
            polling_duration_ms: POLLING_TIMEOUT_MS,
          });
          timeout();
          return false;
        }
        return POLLING_INTERVAL_MS;
      }
      return false;
    },
  });

  // Effect 4: Process search results when stuck in loading
  React.useEffect(() => {
    if (!searchIdeasData || isSearchIdeasLoading || !workspaceId || !assetId) return;
    if (!canProcessSearchResults(state, isGeneratingNew)) return;

    const ideas = searchIdeasData.data;
    if (!ideas?.length) return;

    // Priority 1: Resume polling for in-progress ideas
    const latestInProgress = findLatestInProgressIdea(ideas);
    if (latestInProgress?.id) {
      startPolling(latestInProgress.id);
      return;
    }

    // Priority 2: Use completed idea
    const latestCompleted = findLatestCompletedIdea(ideas);
    if (latestCompleted) {
      if (latestCompleted.output) {
        complete(latestCompleted.output);
      } else {
        captureSparkException(new Error('Completed idea has no output in search results'), 'warning', {
          idea_id: latestCompleted.id,
          asset_id: assetId,
        });
        setError();
      }
    }
  }, [
    state,
    searchIdeasData,
    isSearchIdeasLoading,
    isGeneratingNew,
    workspaceId,
    assetId,
    startPolling,
    complete,
    setError,
  ]);

  const generateIdea = React.useCallback(async () => {
    if (createIdeaMutation.isPending) return;

    addSparkBreadcrumb('Starting Spark idea generation', { asset_id: assetId, workspace_id: workspaceId });
    startLoading();

    try {
      const result = await createIdeaMutation.mutateAsync({
        workspace_id: workspaceId,
        image_id: assetId,
      });

      if (result?.id) {
        addSparkBreadcrumb('Spark idea created, starting poll', { idea_id: result.id, asset_id: assetId });
        startPolling(result.id);
      } else {
        captureSparkException(new Error('No idea ID in create idea response'), 'error', {
          asset_id: assetId,
          idea_id: result?.id ?? 'missing',
        });
        setError();
      }
    } catch (error) {
      if (isSparkNotReadyError(error)) {
        addSparkBreadcrumb('Spark not available for the image yet', {
          asset_id: assetId,
          error_code: getAPIErrorCode(error),
        });
        setUnavailableNotReady();
        return;
      }
      captureSparkException(error, 'error', { asset_id: assetId, workspace_id: workspaceId });
      setError();
    }
  }, [workspaceId, assetId, createIdeaMutation, startLoading, startPolling, setError, setUnavailableNotReady]);

  // Effect 5: Auto-generate on Spark tab when no ideas exist
  // biome-ignore lint/correctness/useExhaustiveDependencies: generateIdea excluded to prevent infinite loops
  React.useEffect(() => {
    if (!searchIdeasData || isSearchIdeasLoading || !workspaceId || !assetId) return;
    if (!canProcessSearchResults(state, isGeneratingNew)) return;

    const ideas = searchIdeasData.data;
    if (shouldAutoGenerate(currentTab, ideas)) {
      void generateIdea();
    }
  }, [searchIdeasData, isSearchIdeasLoading, workspaceId, assetId, currentTab, state, isGeneratingNew]);

  // Effect 6: Handle polling results
  React.useEffect(() => {
    if (!getIdeaData || !currentIdeaId || !isPolling) return;

    const ideaStatus: IdeaStatus = getIdeaData.status;

    switch (ideaStatus) {
      case API_IDEA_STATUS.Requested:
      case API_IDEA_STATUS.Processing:
        break;

      case API_IDEA_STATUS.Completed:
        addSparkBreadcrumb('Spark idea completed successfully', {
          idea_id: currentIdeaId,
          asset_id: assetId,
          output_length: getIdeaData.output?.length,
        });
        if (getIdeaData.output) {
          complete(getIdeaData.output);
        } else {
          captureSparkException(new Error('Completed idea has no output'), 'warning', {
            idea_id: currentIdeaId,
            asset_id: assetId,
          });
          setError();
        }
        break;

      case API_IDEA_STATUS.Failed:
        captureSparkException(new Error('Spark idea generation failed'), 'error', {
          idea_id: currentIdeaId,
          asset_id: assetId,
          status: ideaStatus,
        });
        setError();
        break;

      default:
        captureSparkException(new Error(`Unexpected idea status: ${ideaStatus}`), 'warning', {
          idea_id: currentIdeaId,
          asset_id: assetId,
          status: ideaStatus,
        });
        setError();
    }
  }, [getIdeaData, currentIdeaId, assetId, isPolling, complete, setError]);

  // Effect 7: Handle API errors
  React.useEffect(() => {
    if (searchIdeasError) {
      captureSparkException(searchIdeasError, 'error', { asset_id: assetId });
      setError();
    }
  }, [searchIdeasError, assetId, setError]);

  // Once a failed search succeeds on a later refetch (e.g. window focus), start over so its results get processed
  const hadSearchErrorRef = React.useRef(false);
  React.useEffect(() => {
    if (searchIdeasError) {
      hadSearchErrorRef.current = true;
      return;
    }
    if (!hadSearchErrorRef.current) return;
    hadSearchErrorRef.current = false;
    if (state.status === IdeaStatus_.Error) {
      startLoading();
    }
  }, [searchIdeasError, state.status, startLoading]);

  React.useEffect(() => {
    if (getIdeaError) {
      captureSparkException(getIdeaError, 'error', { asset_id: assetId, idea_id: currentIdeaId });
      setError();
    }
  }, [getIdeaError, assetId, currentIdeaId, setError]);

  // The API turned the image away, and asking again gets the same answer until the asset changes.
  const isNotReady = state.status === IdeaStatus_.UnavailableNotReady;
  const shouldRunGeneration = React.useMemo(
    () => shouldRunSpark && !sparkContent && !currentIdeaId && !isNotReady,
    [shouldRunSpark, sparkContent, currentIdeaId, isNotReady],
  );

  return React.useMemo(
    () => ({ sparkState, sparkContent, generateIdea, shouldRunGeneration }),
    [sparkState, sparkContent, generateIdea, shouldRunGeneration],
  );
}

export { SparkState };
export type { UseSparkDataReturn };
